import { useState, useEffect } from "react";
import { API, useAdmin } from "../App";
import axios from "axios";
import { proxyCoverUrl } from "../utils/coverProxy";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart
} from "recharts";
import {
  TrendingUp, TrendingDown, Eye, Users, Calendar, Clock,
  BookOpen, MessageCircle, Star, Mail, ArrowUpRight
} from "lucide-react";

const COLORS = ["#f43f5e", "#10b981", "#6366f1", "#f59e0b", "#8b5cf6", "#06b6d4", "#ec4899", "#14b8a6", "#f97316"];

const StatCard = ({ title, value, subtitle, icon: Icon, color, change }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm" data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {change !== undefined && change !== 0 && (
        <span className={`flex items-center text-xs font-semibold px-2 py-1 rounded-full ${change > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
          {change > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {Math.abs(change)}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
    <p className="text-gray-500 text-xs mt-1">{title}</p>
    {subtitle && <p className="text-gray-400 text-[10px] mt-0.5">{subtitle}</p>}
  </div>
);

const ActivityIcon = ({ type }) => {
  const config = {
    feedback: { icon: MessageCircle, bg: "bg-emerald-100", color: "text-emerald-600" },
    story: { icon: Star, bg: "bg-amber-100", color: "text-amber-600" },
    contact: { icon: Mail, bg: "bg-blue-100", color: "text-blue-600" },
    conversation: { icon: MessageCircle, bg: "bg-purple-100", color: "text-purple-600" },
  };
  const c = config[type] || config.feedback;
  return (
    <div className={`w-8 h-8 ${c.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
      <c.icon className={`w-4 h-4 ${c.color}`} />
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsTab() {
  const { adminToken } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${adminToken}` },
          withCredentials: true
        });
        setData(res.data);
      } catch (e) {
        console.error("Failed to fetch analytics:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [adminToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-rose-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-center text-gray-400 py-12">Unable to load analytics data.</p>;
  }

  const { traffic, daily, categories, top_books, recent_activity } = data;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Traffic Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Today's Views"
          value={traffic.today_views}
          subtitle={`${traffic.today_unique} unique visitors`}
          icon={Eye}
          color="bg-rose-500"
        />
        <StatCard
          title="This Week"
          value={traffic.this_week_views}
          subtitle={`${traffic.this_week_unique} unique visitors`}
          icon={Calendar}
          color="bg-indigo-500"
          change={traffic.week_change_pct}
        />
        <StatCard
          title="All Time Views"
          value={traffic.total_views}
          subtitle={`${traffic.total_unique} total unique visitors`}
          icon={Users}
          color="bg-emerald-500"
        />
        <StatCard
          title="Avg. Daily Views"
          value={traffic.avg_daily_views}
          subtitle="This week average"
          icon={Clock}
          color="bg-amber-500"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Visitor Trend */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="daily-trend-chart">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Daily Visitor Trends</h3>
          {daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="uniqueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickFormatter={(d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="views" name="Page Views" stroke="#f43f5e" strokeWidth={2} fill="url(#viewsGrad)" />
                <Area type="monotone" dataKey="unique" name="Unique Visitors" stroke="#6366f1" strokeWidth={2} fill="url(#uniqueGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-center py-16">No visitor data yet. Share your site to start tracking!</p>
          )}
          <div className="flex items-center gap-6 mt-2 justify-center">
            <span className="flex items-center gap-2 text-xs text-gray-500">
              <span className="w-3 h-1 rounded bg-rose-500" /> Page Views
            </span>
            <span className="flex items-center gap-2 text-xs text-gray-500">
              <span className="w-3 h-1 rounded bg-indigo-500" /> Unique Visitors
            </span>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="category-chart">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Books by Category</h3>
          {categories.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={categories}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                  >
                    {categories.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5 max-h-[120px] overflow-y-auto">
                {categories.map((cat, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      {cat.name}
                    </span>
                    <span className="font-semibold text-gray-700">{cat.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-gray-400 text-sm text-center py-12">No category data</p>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Viewed Books */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="top-books">
          <h3 className="text-base font-semibold text-gray-800 mb-4">
            <BookOpen className="w-4 h-4 inline mr-2 text-rose-500" />
            Most Viewed Books
          </h3>
          {top_books.length > 0 ? (
            <div className="space-y-3">
              {top_books.map((book, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${i < 3 ? 'bg-rose-500' : 'bg-gray-400'}`}>
                    {i + 1}
                  </span>
                  <div className="w-8 h-11 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    {book.cover_image && (
                      <img src={proxyCoverUrl(book.cover_image)} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{book.title}</p>
                    <p className="text-xs text-gray-500">{book.author}</p>
                  </div>
                  <span className="flex items-center gap-1 text-sm font-semibold text-gray-600">
                    <Eye className="w-3.5 h-3.5 text-gray-400" />
                    {book.views}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <Eye className="w-10 h-10 mx-auto text-gray-200 mb-2" />
              <p className="text-gray-400 text-sm">Book view tracking has been enabled.</p>
              <p className="text-gray-300 text-xs mt-1">Data will appear as visitors browse books.</p>
            </div>
          )}
        </div>

        {/* Recent Activity Feed */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="recent-activity">
          <h3 className="text-base font-semibold text-gray-800 mb-4">
            <ArrowUpRight className="w-4 h-4 inline mr-2 text-indigo-500" />
            Recent Activity
          </h3>
          {recent_activity.length > 0 ? (
            <div className="space-y-3">
              {recent_activity.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <ActivityIcon type={item.type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{item.name || "Anonymous"}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        item.type === 'feedback' ? 'bg-emerald-50 text-emerald-600' :
                        item.type === 'story' ? 'bg-amber-50 text-amber-600' :
                        item.type === 'contact' ? 'bg-blue-50 text-blue-600' :
                        'bg-purple-50 text-purple-600'
                      }`}>
                        {item.type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{item.text}</p>
                    {item.date && (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-10">No recent activity yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
